import { useCallback, useEffect, useState } from "react";
import { api, hrDateTime } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Bell, RefreshCw, Loader2, AlertTriangle, Search } from "lucide-react";
import { toast } from "sonner";

/**
 * Iter28: Reminder & escalation overview for tajništvo.
 *
 * Fetches /api/admin/reminder-history?year={Y} and renders:
 *  - Summary counts (total reminded, total escalated, % escalated)
 *  - Sortable table: Zaposlenik | # podsjetnika | Eskaliran? | Manager CC | Posljednji put
 *  - Search box (filter by email)
 *  - Year selector (defaults to current)
 *  - "Pokreni odmah" button (manual trigger of the cron job) — admin only
 */
export function ReminderHistoryTab({ isAdmin }) {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState({ year: currentYear, total: 0, escalated: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/reminder-history?year=${year}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri dohvaćanju");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/admin/run-job/remind_pending_employees`);
      const result = r.data?.result || {};
      if (result.skipped) {
        toast.info(
          result.reason === "outside_window"
            ? `Izvan prozora — ${result.days_until} dana do kolektivnog odmora (slanje aktivno tek u zadnja 3 tjedna).`
            : `Preskočeno: ${result.reason}`
        );
      } else {
        toast.success(`Poslano ${result.sent}, eskalirano ${result.escalated}, preskočeno ${result.skipped_already_submitted}, greška ${result.failed}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška");
    } finally {
      setRunning(false);
    }
  };

  const filtered = data.items.filter(it =>
    !search.trim() || it.employee_email.toLowerCase().includes(search.toLowerCase())
  );
  const pct = data.total > 0 ? Math.round((data.escalated / data.total) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="reminder-history-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Bell className="w-5 h-5 text-primary"/>
          <div>
            <h2 className="font-medium text-base">Podsjetnici za godišnji odmor</h2>
            <p className="text-xs text-muted-foreground">
              Pregled poslanih podsjetnika po zaposleniku i status eskalacije (CC nadređenom nakon 4. podsjetnika).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number" min={2024} max={2099} value={year}
            onChange={(e) => setYear(Number(e.target.value) || currentYear)}
            className="w-24" data-testid="reminder-year-input"
          />
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="reminder-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <RefreshCw className="w-4 h-4 mr-1"/>}
            Osvježi
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={runNow} disabled={running} data-testid="reminder-run-now">
              {running ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Bell className="w-4 h-4 mr-1"/>}
              Pokreni odmah
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground">Ukupno podsjetnika</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="reminder-total">{data.total}</div>
            <div className="text-[11px] text-muted-foreground">zaposlenika kojima je poslan barem 1 podsjetnik</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-amber-600"/>Eskalirano
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600" data-testid="reminder-escalated">{data.escalated}</div>
            <div className="text-[11px] text-muted-foreground">manager CC-an u mailu ({pct}% od ukupnih)</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs uppercase tracking-wide text-muted-foreground">Godina</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.year}</div>
            <div className="text-[11px] text-muted-foreground">{data.items.length === 0 ? "Nema podataka" : "aktivni unosi"}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
            <Input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Pretraži po e-mailu..."
              className="pl-9" data-testid="reminder-search"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground p-6 text-center">
              {data.items.length === 0
                ? "Još nema poslanih podsjetnika za ovu godinu. Job se aktivira automatski 21 dan prije kolektivnog odmora."
                : "Nema rezultata za pretragu."}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-secondary/60">
                <tr>
                  <th className="text-left p-3">Zaposlenik</th>
                  <th className="text-center p-3"># podsjetnika</th>
                  <th className="text-center p-3">Status</th>
                  <th className="text-left p-3">Manager CC</th>
                  <th className="text-left p-3">Posljednji put</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.employee_email} className="border-t border-border" data-testid={`reminder-row-${it.employee_email}`}>
                    <td className="p-3 text-xs">{it.employee_email}</td>
                    <td className="p-3 text-center font-medium">{it.count}</td>
                    <td className="p-3 text-center">
                      {it.escalated_at ? (
                        <Badge variant="destructive" data-testid="reminder-escalated-badge">
                          <AlertTriangle className="w-3 h-3 mr-1"/>Eskalirano
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Aktivno</Badge>
                      )}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">
                      {it.escalated_to || "—"}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">
                      {it.last_sent_at ? hrDateTime(it.last_sent_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
